import React, { useCallback, useEffect, useState } from "react";
import { convertToCompanyTimezoneObject } from "../../../../../lib/functions";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  message,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  ApiOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";

import {
  generateCrmApiKeyApi,
  getCrmApiKeysApi,
  revokeCrmApiKeyApi,
  updateCrmApiKeyPermissionsApi,
} from "../../../../../apiservice/crm";

import "./apiIntegration.css";

import GenerateApiKeyModal from "./GenerateApiKeyModal";
import ApiKeyRevealModal from "./ApiKeyRevealModal";
import EditPermissionsModal from "./EditPermissionsModal";

const { Title, Text } = Typography;

const ApiKeys = ({ users = [] }) => {
  const [loading, setLoading] = useState(false);
  const [keys, setKeys] = useState([]);

  const [generateOpen, setGenerateOpen] = useState(false);

  const [generatedKey, setGeneratedKey] = useState(null);

  const [selectedKey, setSelectedKey] = useState(null);
  const [permissionOpen, setPermissionOpen] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getCrmApiKeysApi();     

      setKeys(res?.data?.data || []);
    } catch (err) {
      console.error(err);

      message.error(
        err?.response?.data?.message ||
          "Failed to load API keys"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleGenerated = async (data) => {
    setGenerateOpen(false);

    setGeneratedKey(data);

    await loadKeys();
  };

  const handleEditPermissions = (record) => {
    setSelectedKey(record);
    setPermissionOpen(true);
  };

  const handlePermissionSave = async ({
    permissions,
    assignment_mode,
    default_assigned_to,
    assignment_pool,
  }) => {
    if (!selectedKey?.id) return;

    try {
      await updateCrmApiKeyPermissionsApi({
        id: selectedKey.id,
        permissions,
        assignment_mode,
        default_assigned_to,
        assignment_pool,
      });

      message.success("API key settings updated");

      setPermissionOpen(false);
      setSelectedKey(null);

      await loadKeys();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          "Failed to update API key settings"
      );
    }
  };

  const handleRevoke = async (record) => {
    try {
      await revokeCrmApiKeyApi({
        id: record.id,
      });

      message.success("API key revoked");

      await loadKeys();
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          "Failed to revoke API key"
      );
    }
  };

  const activeCount = keys.filter(
    (item) => item.status === "active"
  ).length;

  const revokedCount = keys.filter(
    (item) => item.status === "revoked"
  ).length;

  const renderPermissions = (permissions) => {
    let parsed = permissions;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch {
        parsed = {};
      }
    }

    const enabled = [];

    Object.entries(parsed || {}).forEach(
      ([resource, actions]) => {
        Object.entries(actions || {}).forEach(
          ([action, allowed]) => {
            if (allowed) {
              enabled.push(`${resource}.${action}`);
            }
          }
        );
      }
    );

    if (!enabled.length) {
      return <Text type="secondary">No permissions</Text>;
    }

    return (
      <Space wrap size={[4, 4]}>
        {enabled.slice(0, 3).map((permission) => (
          <Tag key={permission}>
            {permission}
          </Tag>
        ))}

        {enabled.length > 3 && (
          <Tooltip title={enabled.slice(3).join(", ")}>
            <Tag>+{enabled.length - 3}</Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: "Integration",
      key: "name",
      render: (_, record) => (
        <Space>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#f5f5f5",
            }}
          >
            <KeyOutlined />
          </div>

          <div>
            <div style={{ fontWeight: 600 }}>
              {record.name || "API Integration"}
            </div>

            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.key_prefix || "API Key"}
            </Text>
          </div>
        </Space>
      ),
    },

    {
      title: "Permissions",
      dataIndex: "permissions",
      key: "permissions",
      render: renderPermissions,
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "red"}>
          {status === "active" ? "Active" : "Revoked"}
        </Tag>
      ),
    },

    {
      title: "Leads Assigned To",
      key: "assigned_to",
      render: (_, record) => {
        if (record.assignment_mode === "round_robin") {
          const names = (record.assignment_pool || [])
            .map((id) => users.find((u) => u.id === id)?.full_name)
            .filter(Boolean);

          if (!names.length) {
            return <Text type="secondary">Round robin (no agents selected)</Text>;
          }

          return (
            <Tooltip title={`Round robin: ${names.join(", ")}`}>
              <Tag color="blue">Round robin</Tag>{" "}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {names.length} agent{names.length > 1 ? "s" : ""}
              </Text>
            </Tooltip>
          );
        }

        if (record.assignment_mode === "workload") {
          return (
            <Tooltip title="Each lead goes to whoever among your active agents currently has the lightest load.">
              <Tag color="purple">Auto (workload balanced)</Tag>
            </Tooltip>
          );
        }

        return record.default_assigned_to_name ? (
          <span>{record.default_assigned_to_name}</span>
        ) : (
          <Tooltip title={`No default set — falls back to whoever generated this key${record.created_by_name ? ` (${record.created_by_name})` : ""}`}>
            <Text type="secondary">
              {record.created_by_name ? `${record.created_by_name} (creator)` : "Key creator"}
            </Text>
          </Tooltip>
        );
      },
    },

    {
      title: "Last Used",
      dataIndex: "last_used_at",
      key: "last_used_at",
      render: (value) =>
        value ? convertToCompanyTimezoneObject(value).format("DD MMM YYYY, hh:mm A") : "Never",
    },

    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (value) =>
        value ? convertToCompanyTimezoneObject(value).format("DD MMM YYYY") : "-",
    },

    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, record) => {
        const active = record.status === "active";

        return (
          <Space>
            <Tooltip title="Edit permissions">
              <Button
                icon={<EditOutlined />}
                disabled={!active}
                onClick={() =>
                  handleEditPermissions(record)
                }
              />
            </Tooltip>

            <Popconfirm
              title="Revoke API Key?"
              description={
                <>
                  Any website or application using this key
                  will immediately lose API access.
                </>
              }
              okText="Revoke"
              okButtonProps={{ danger: true }}
              cancelText="Cancel"
              disabled={!active}
              onConfirm={() => handleRevoke(record)}
            >
              <Tooltip title="Revoke API key">
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!active}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Row
        justify="space-between"
        align="middle"
        gutter={[16, 16]}
        style={{ marginBottom: 20 }}
      >
        <Col>
          <Title level={5} style={{ margin: 0 }}>
            API Key Management
          </Title>

          <Text type="secondary">
            Generate and manage API credentials for external integrations.
          </Text>
        </Col>

        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate API Key
          </Button>
        </Col>
      </Row>

      <Alert
        type="info"
        showIcon
        icon={<SafetyCertificateOutlined />}
        message="Secure API Access"
        description={
          <>
            API keys provide branch-specific access to CRM data. Only share keys
            with trusted applications and grant only the permissions they
            require.
          </>
        }
        style={{ marginBottom: 20 }}
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total API Keys"
              value={keys.length}
              prefix={<ApiOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Active Keys" value={activeCount} />
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Revoked Keys" value={revokedCount} />
          </Card>
        </Col>
      </Row>

      <Card
        title="API Keys"
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Spin spinning={loading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={keys}
            pagination={
              keys.length > 10
                ? {
                    pageSize: 10,
                  }
                : false
            }
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No API keys generated yet"
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setGenerateOpen(true)}
                  >
                    Generate First API Key
                  </Button>
                </Empty>
              ),
            }}
            scroll={{ x: 1050 }}
          />
        </Spin>
      </Card>

      <GenerateApiKeyModal
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={handleGenerated}
        users={users}
      />

      <ApiKeyRevealModal
        open={!!generatedKey}
        data={generatedKey}
        onClose={() => setGeneratedKey(null)}
      />

      <EditPermissionsModal
        open={permissionOpen}
        apiKey={selectedKey}
        onClose={() => {
          setPermissionOpen(false);
          setSelectedKey(null);
        }}
        onSave={handlePermissionSave}
        users={users}
      />
    </div>
  );
};

export default ApiKeys;